import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Input, Form, Typography,
  Checkbox, Alert, Row, Col, DatePicker, App as AntApp, Flex, Empty,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { API_URL } from '../../config';
import type { EmpresaDados } from './types';
import { getToken } from './utils';
import LogoUploader from './LogoUploader';

const { Title, Text } = Typography;

// Cadastro institucional (razão, endereço, contatos, registros, bancos, representantes).
export default function AdminEmpresa() {
  const { message } = AntApp.useApp();
  const [dados, setDados] = useState<EmpresaDados | null>(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [regenInfo, setRegenInfo] = useState<number>(0);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_obter_empresa' }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setDados(d);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-line */ }, []);

  function set<K extends keyof EmpresaDados>(k: K, v: EmpresaDados[K]) {
    if (!dados) return;
    setDados({ ...dados, [k]: v });
  }

  async function salvar() {
    if (!dados) return;
    setSalvando(true); setRegenInfo(0);
    try {
      const token = await getToken();
      const payload: any = { action: 'adm_salvar_empresa' };
      const omitir = ['id', 'atualizado_em', 'atualizado_por', 'logo_url', 'docs_regenerados'];
      for (const k of Object.keys(dados)) {
        if (omitir.includes(k)) continue;
        payload[k] = (dados as any)[k];
      }
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, payload,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setDados(d);
      message.success('Salvo.');
      if (d.docs_regenerados) {
        setRegenInfo(d.docs_regenerados);
        setTimeout(() => setRegenInfo(0), 6000);
      }
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  if (loading) return <Card loading />;
  if (!dados) return <Empty description="Dados não disponíveis." />;

  const addRegistro = () => set('outros_registros', [...(dados.outros_registros || []), { tipo: '', numero: '' }]);
  const setRegistro = (i: number, k: string, v: string) => {
    const arr = [...(dados.outros_registros || [])];
    arr[i] = { ...arr[i], [k]: v };
    set('outros_registros', arr);
  };
  const delRegistro = (i: number) => set('outros_registros', (dados.outros_registros || []).filter((_, j) => j !== i));

  const addBanco = () => set('bancos', [...(dados.bancos || []), { banco: '', agencia: '', conta: '' }]);
  const setBanco = (i: number, k: string, v: string) => {
    const arr = [...(dados.bancos || [])];
    arr[i] = { ...arr[i], [k]: v };
    set('bancos', arr);
  };
  const delBanco = (i: number) => set('bancos', (dados.bancos || []).filter((_, j) => j !== i));

  const addRep = () => set('representantes', [...(dados.representantes || []), { nome: '', cpf: '', cargo: '' }]);
  const setRep = (i: number, k: string, v: any) => {
    const arr = [...(dados.representantes || [])];
    arr[i] = { ...arr[i], [k]: v };
    set('representantes', arr);
  };
  const delRep = (i: number) => set('representantes', (dados.representantes || []).filter((_, j) => j !== i));

  const txt = (k: keyof EmpresaDados, ph?: string) => (
    <Input value={(dados[k] as string) || ''} placeholder={ph}
      onChange={e => set(k, e.target.value as any)} size="small" />
  );

  return (
    <div>
      <Flex align="center" gap={12} style={{ marginBottom: 14 }} wrap="wrap">
        <Title level={5} style={{ margin: 0, color: '#020D7F' }}>🏢 Cadastro institucional</Title>
        <Text type="secondary" style={{ fontSize: 11, flex: 1 }}>
          Esses dados aparecem em propostas, contratos e documentos fiscais.
        </Text>
        {dados.atualizado_em && (
          <Text type="secondary" style={{ fontSize: 10 }}>
            Atualizado em {new Date(dados.atualizado_em).toLocaleString('pt-BR')}
            {dados.atualizado_por ? ` por ${dados.atualizado_por}` : ''}
          </Text>
        )}
      </Flex>

      <Card size="small" title="Identificação" style={{ marginBottom: 14 }}>
        <Row gutter={10}>
          <Col xs={24} md={12}><Form.Item label="Razão Social" style={{ marginBottom: 8 }}>{txt('razao_social')}</Form.Item></Col>
          <Col xs={24} md={12}><Form.Item label="Nome Fantasia" style={{ marginBottom: 8 }}>{txt('nome_fantasia')}</Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="CNPJ" style={{ marginBottom: 8 }}>{txt('cnpj', '00.000.000/0001-00')}</Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="Inscrição Estadual" style={{ marginBottom: 8 }}>{txt('inscricao_estadual')}</Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="Inscrição Municipal" style={{ marginBottom: 8 }}>{txt('inscricao_municipal')}</Form.Item></Col>
        </Row>
      </Card>

      <Card size="small" title="Logo" style={{ marginBottom: 14 }}>
        <LogoUploader dados={dados} setDados={setDados as any} />
      </Card>

      <Card size="small" title="Endereço" style={{ marginBottom: 14 }}>
        <Row gutter={10}>
          <Col xs={24} md={12}><Form.Item label="Logradouro" style={{ marginBottom: 8 }}>{txt('logradouro')}</Form.Item></Col>
          <Col xs={12} md={4}><Form.Item label="Número" style={{ marginBottom: 8 }}>{txt('numero')}</Form.Item></Col>
          <Col xs={12} md={8}><Form.Item label="Complemento" style={{ marginBottom: 8 }}>{txt('complemento')}</Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="Bairro" style={{ marginBottom: 8 }}>{txt('bairro')}</Form.Item></Col>
          <Col xs={12} md={8}><Form.Item label="Cidade" style={{ marginBottom: 8 }}>{txt('cidade')}</Form.Item></Col>
          <Col xs={6} md={4}><Form.Item label="UF" style={{ marginBottom: 8 }}>{txt('uf')}</Form.Item></Col>
          <Col xs={6} md={4}><Form.Item label="CEP" style={{ marginBottom: 8 }}>{txt('cep', '00000-000')}</Form.Item></Col>
        </Row>
      </Card>

      <Card size="small" title="Contatos" style={{ marginBottom: 14 }}>
        <Row gutter={10}>
          <Col xs={24} md={8}><Form.Item label="Telefone principal" style={{ marginBottom: 8 }}>{txt('telefone_principal')}</Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="Telefone secundário" style={{ marginBottom: 8 }}>{txt('telefone_secundario')}</Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="Website" style={{ marginBottom: 8 }}>{txt('website', 'https://...')}</Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="E-mail geral" style={{ marginBottom: 8 }}>{txt('email_geral')}</Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="E-mail comercial" style={{ marginBottom: 8 }}>{txt('email_comercial')}</Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="E-mail financeiro" style={{ marginBottom: 8 }}>{txt('email_financeiro')}</Form.Item></Col>
        </Row>
      </Card>

      <Card size="small" title="Automações de e-mail" style={{ marginBottom: 14 }}>
        {/* O campo "E-mail responsável por emissão de NF" morou aqui até
            2026-09-01. Editar aqui deixou de ter efeito: quem recebe cada
            aviso agora é cadastrado na tela do Financeiro. */}
        <Text type="secondary" style={{ fontSize: 12 }}>
          Quem recebe os avisos automáticos do financeiro (NF a emitir, contas
          vencendo, reembolso para revisar) agora é cadastrado em{' '}
          <Link to="/financeiro/cadastros?aba=avisos">Financeiro › Cadastros › Avisos</Link>.
        </Text>
      </Card>

      <Card size="small" title="Registros profissionais" style={{ marginBottom: 14 }}>
        <Row gutter={10}>
          <Col xs={24} md={8}><Form.Item label="CREA — número" style={{ marginBottom: 8 }}>{txt('crea_numero')}</Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="CREA — UF" style={{ marginBottom: 8 }}>{txt('crea_uf')}</Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="CREA — validade" style={{ marginBottom: 8 }}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
              value={dados.crea_validade ? dayjs(dados.crea_validade) : null}
              onChange={(d) => set('crea_validade', d ? d.format('YYYY-MM-DD') as any : null)} />
          </Form.Item></Col>
        </Row>
        <Text strong style={{ fontSize: 11, color: '#666', display: 'block', marginTop: 8, marginBottom: 6 }}>
          Outros registros (CAU, etc.)
        </Text>
        {(dados.outros_registros || []).map((reg, i) => (
          <Row key={i} gutter={6} style={{ marginBottom: 6 }}>
            <Col span={6}><Input size="small" value={reg.tipo} placeholder="Tipo (CAU, CFT…)" onChange={e => setRegistro(i, 'tipo', e.target.value)} /></Col>
            <Col span={9}><Input size="small" value={reg.numero} placeholder="Número" onChange={e => setRegistro(i, 'numero', e.target.value)} /></Col>
            <Col span={3}><Input size="small" value={reg.uf || ''} placeholder="UF" onChange={e => setRegistro(i, 'uf', e.target.value)} /></Col>
            <Col span={4}><DatePicker size="small" style={{ width: '100%' }} format="DD/MM/YYYY"
              value={reg.validade ? dayjs(reg.validade) : null}
              onChange={(d) => setRegistro(i, 'validade', d ? d.format('YYYY-MM-DD') : '')} /></Col>
            <Col span={2}><Button size="small" danger icon={<DeleteOutlined />} onClick={() => delRegistro(i)} /></Col>
          </Row>
        ))}
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={addRegistro} style={{ marginTop: 4 }}>
          Adicionar registro
        </Button>
      </Card>

      <Card size="small" title="Dados bancários" style={{ marginBottom: 14 }}>
        {(dados.bancos || []).map((b, i) => (
          <Row key={i} gutter={6} style={{ marginBottom: 6 }}>
            <Col span={6}><Input size="small" value={b.banco} placeholder="Banco" onChange={e => setBanco(i, 'banco', e.target.value)} /></Col>
            <Col span={3}><Input size="small" value={b.agencia} placeholder="Agência" onChange={e => setBanco(i, 'agencia', e.target.value)} /></Col>
            <Col span={5}><Input size="small" value={b.conta} placeholder="Conta" onChange={e => setBanco(i, 'conta', e.target.value)} /></Col>
            <Col span={4}><Input size="small" value={b.tipo || ''} placeholder="cc/poupança" onChange={e => setBanco(i, 'tipo', e.target.value)} /></Col>
            <Col span={4}><Input size="small" value={b.pix || ''} placeholder="Chave PIX" onChange={e => setBanco(i, 'pix', e.target.value)} /></Col>
            <Col span={2}><Button size="small" danger icon={<DeleteOutlined />} onClick={() => delBanco(i)} /></Col>
          </Row>
        ))}
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={addBanco} style={{ marginTop: 4 }}>
          Adicionar conta
        </Button>
      </Card>

      <Card size="small" title="Representantes legais (assinatura de contratos)" style={{ marginBottom: 14 }}>
        {(dados.representantes || []).map((rep, i) => (
          <Row key={i} gutter={6} style={{ marginBottom: 6 }} align="middle">
            <Col span={8}><Input size="small" value={rep.nome} placeholder="Nome completo" onChange={e => setRep(i, 'nome', e.target.value)} /></Col>
            <Col span={6}><Input size="small" value={rep.cpf} placeholder="CPF" onChange={e => setRep(i, 'cpf', e.target.value)} /></Col>
            <Col span={5}><Input size="small" value={rep.cargo} placeholder="Cargo" onChange={e => setRep(i, 'cargo', e.target.value)} /></Col>
            <Col span={3}>
              <Checkbox checked={!!rep.principal} onChange={e => setRep(i, 'principal', e.target.checked)}>
                <Text style={{ fontSize: 11 }}>principal</Text>
              </Checkbox>
            </Col>
            <Col span={2}><Button size="small" danger icon={<DeleteOutlined />} onClick={() => delRep(i)} /></Col>
          </Row>
        ))}
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={addRep} style={{ marginTop: 4 }}>
          Adicionar representante
        </Button>
      </Card>

      {regenInfo > 0 && (
        <Alert type="info" showIcon style={{ marginBottom: 10 }}
          message={`${regenInfo} documento(s) de venda atualizado(s) automaticamente.`} />
      )}

      <Flex justify="flex-end" gap={10}>
        <Button onClick={carregar} icon={<ReloadOutlined />}>Recarregar</Button>
        <Button type="primary" loading={salvando} onClick={salvar}>Salvar</Button>
      </Flex>
    </div>
  );
}
